import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Card, CardContent, Grid, TextField, 
  Select, MenuItem, FormControl, InputLabel, Chip, Avatar, List, ListItem, 
  ListItemText, ListItemAvatar, ListItemSecondaryAction, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar,
  CircularProgress, Divider, Tabs, Tab, Switch, FormControlLabel,
  Autocomplete, Checkbox, FormGroup, RadioGroup, Radio, FormLabel
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  People as PeopleIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Message as MessageIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CloudQueue as CloudQueueIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

type ManualApiSelection = 'z-api' | 'cloud-api';

interface WhatsAppInstance {
  id: string;
  name: string;
  instance_name: string;
  phone_number: string;
  status: string;
  is_connected: boolean;
  daily_sent: number;
  daily_limit: number;
  provider?: 'z-api' | 'evolution';
}

interface CloudApiConfigOption {
  id: string;
  name: string;
  phone_number?: string;
  display_phone_number?: string;
  status?: string;
  daily_sent?: number;
  daily_limit?: number;
}

/** Preset cadastrado para envio manual Cloud API (por conta Meta). */
interface CloudManualTemplatePreset {
  id: string;
  cloud_api_config_id: string;
  display_label: string;
  template_name: string;
  language_code: string;
  components: unknown[] | null;
}

interface CrmStage {
  id: string;
  name: string;
  color: string;
  pipeline_id: string;
}

interface CrmPipeline {
  id: string;
  name: string;
  color: string;
}

interface Contact {
  id: string;
  name: string;
  phone: string;
  email?: string;
  avatar_url?: string;
  source: string;
  stage_id: string;
  stage_name: string;
  pipeline_name: string;
  pipeline_id: string;
  created_at: string;
}

const WhatsAppManual: React.FC = () => {
  const API_BASE_URL = getApiUrl();
  
  const [selectedApi, setSelectedApi] = useState<ManualApiSelection | null>(() => {
    const saved = localStorage.getItem('whatsapp-manual-api-selection');
    if (saved === 'evolution-api') return null;
    return saved === 'z-api' || saved === 'cloud-api' ? saved : null;
  });
  
  // Estados principais
  const [instances, setInstances] = useState<WhatsAppInstance[]>([]);
  const [cloudConfigs, setCloudConfigs] = useState<CloudApiConfigOption[]>([]);
  const [crmStages, setCrmStages] = useState<CrmStage[]>([]);
  const [crmPipelines, setCrmPipelines] = useState<CrmPipeline[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  
  // Estados de controle
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  
  // Estados do formulário
  const [activeTab, setActiveTab] = useState(0);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [messageText, setMessageText] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('pt_BR');
  const [templateComponentsJson, setTemplateComponentsJson] = useState('');
  const [cloudPresets, setCloudPresets] = useState<CloudManualTemplatePreset[]>([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [selectedPresetOption, setSelectedPresetOption] = useState<CloudManualTemplatePreset | null>(null);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetSaving, setPresetSaving] = useState(false);
  const [presetForm, setPresetForm] = useState({
    editingId: null as string | null,
    display_label: '',
    template_name: '',
    language_code: 'pt_BR',
    components_json: '',
  });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedPipelines, setSelectedPipelines] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyWithWhatsApp, setShowOnlyWithWhatsApp] = useState(true);
  
  // Estados de diálogo
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(null);
  };

  // Carregar dados iniciais
  useEffect(() => {
    if (!selectedApi) return;
    loadData();
  }, [selectedApi]);

  useEffect(() => {
    setSelectedInstance('');
  }, [selectedApi]);

  // Recarregar contatos quando filtros mudarem
  useEffect(() => {
    if (crmStages.length > 0 && crmPipelines.length > 0) {
      loadContacts();
    }
  }, [selectedStages, selectedPipelines, searchTerm, showOnlyWithWhatsApp]);

  const loadData = async () => {
    try {
      setLoading(true);

      if (selectedApi === 'cloud-api') {
        const configsResponse = await axios.get(`${API_BASE_URL}/whatsapp-cloud-api/configs`);
        const raw = configsResponse.data?.data || [];
        const active = raw.filter((c: CloudApiConfigOption) => c.status === 'active');
        setCloudConfigs(active);
        setInstances([]);
      } else {
        const instancesResponse = await axios.get(`${API_BASE_URL}/whatsapp-manual/instances`);
        const all = instancesResponse.data.data || [];
        setInstances(
          all.filter(
            (i: WhatsAppInstance) => !i.provider || i.provider === 'z-api'
          )
        );
        setCloudConfigs([]);
      }

      // Carregar estágios CRM
      const stagesResponse = await axios.get(`${API_BASE_URL}/crm/stages`);
      setCrmStages(stagesResponse.data.data || []);

      // Carregar pipelines CRM
      const pipelinesResponse = await axios.get(`${API_BASE_URL}/crm/pipelines`);
      setCrmPipelines(pipelinesResponse.data.data || []);

      // Carregar contatos iniciais (sem filtros)
      await loadContacts();

    } catch (err: any) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
      showSnackbar('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const params = new URLSearchParams();
      
      if (selectedStages.length > 0) {
        selectedStages.forEach(stage => params.append('stages', stage));
      }
      
      if (selectedPipelines.length > 0) {
        selectedPipelines.forEach(pipeline => params.append('pipelines', pipeline));
      }
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (showOnlyWithWhatsApp) {
        params.append('with_whatsapp', 'true');
      }

      const contactsResponse = await axios.get(`${API_BASE_URL}/whatsapp-manual/contacts?${params.toString()}`);
      setContacts(contactsResponse.data.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar contatos:', err);
      showSnackbar('Erro ao carregar contatos', 'error');
    }
  };

  const loadCloudPresets = useCallback(async () => {
    if (selectedApi !== 'cloud-api' || !selectedInstance) {
      setCloudPresets([]);
      return;
    }
    setLoadingPresets(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/whatsapp-manual/cloud-template-presets`, {
        params: { cloud_api_config_id: selectedInstance },
      });
      setCloudPresets(res.data?.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar presets Cloud API:', err);
      setCloudPresets([]);
    } finally {
      setLoadingPresets(false);
    }
  }, [selectedApi, selectedInstance, API_BASE_URL]);

  useEffect(() => {
    loadCloudPresets();
  }, [loadCloudPresets]);

  useEffect(() => {
    setSelectedPresetOption(null);
  }, [selectedInstance]);

  const resetPresetFormFields = () => {
    setPresetForm({
      editingId: null,
      display_label: '',
      template_name: '',
      language_code: 'pt_BR',
      components_json: '',
    });
  };

  const openPresetManager = () => {
    if (!selectedInstance) {
      showSnackbar('Selecione uma conta Cloud API primeiro', 'error');
      return;
    }
    resetPresetFormFields();
    setPresetDialogOpen(true);
  };

  const fillPresetFormFromSendFields = () => {
    setPresetForm((prev) => ({
      ...prev,
      template_name: templateName.trim(),
      language_code: templateLanguage.trim() || 'pt_BR',
      components_json: templateComponentsJson.trim(),
    }));
  };

  const editCloudPreset = (p: CloudManualTemplatePreset) => {
    setPresetForm({
      editingId: p.id,
      display_label: p.display_label,
      template_name: p.template_name,
      language_code: p.language_code || 'pt_BR',
      components_json:
        p.components && Array.isArray(p.components) && p.components.length > 0
          ? JSON.stringify(p.components, null, 2)
          : '',
    });
  };

  const saveCloudPreset = async () => {
    if (!selectedInstance) return;
    const label = presetForm.display_label.trim();
    const tname = presetForm.template_name.trim();
    if (!label || !tname) {
      showSnackbar('Preencha o nome na lista e o nome no Meta', 'error');
      return;
    }
    let componentsPayload: string | undefined = presetForm.components_json.trim();
    if (componentsPayload) {
      try {
        const parsed = JSON.parse(componentsPayload);
        if (!Array.isArray(parsed)) {
          showSnackbar('Componentes devem ser um array JSON', 'error');
          return;
        }
      } catch {
        showSnackbar('JSON dos componentes inválido', 'error');
        return;
      }
    } else {
      componentsPayload = undefined;
    }

    setPresetSaving(true);
    try {
      if (presetForm.editingId) {
        await axios.put(`${API_BASE_URL}/whatsapp-manual/cloud-template-presets/${presetForm.editingId}`, {
          display_label: label,
          template_name: tname,
          language_code: presetForm.language_code.trim() || 'pt_BR',
          components: componentsPayload,
        });
        showSnackbar('Preset atualizado', 'success');
      } else {
        await axios.post(`${API_BASE_URL}/whatsapp-manual/cloud-template-presets`, {
          cloud_api_config_id: selectedInstance,
          display_label: label,
          template_name: tname,
          language_code: presetForm.language_code.trim() || 'pt_BR',
          components: componentsPayload,
        });
        showSnackbar('Preset criado', 'success');
      }
      resetPresetFormFields();
      await loadCloudPresets();
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao salvar preset', 'error');
    } finally {
      setPresetSaving(false);
    }
  };

  const deleteCloudPreset = async (id: string) => {
    if (!window.confirm('Remover este template salvo da lista?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/whatsapp-manual/cloud-template-presets/${id}`);
      showSnackbar('Removido', 'success');
      setPresetForm((prev) =>
        prev.editingId === id ? { ...prev, editingId: null, display_label: '', template_name: '', language_code: 'pt_BR', components_json: '' } : prev
      );
      setSelectedPresetOption(null);
      await loadCloudPresets();
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao remover', 'error');
    }
  };


  const handleContactToggle = (contact: Contact) => {
    setSelectedContacts(prev => {
      const isSelected = prev.some(c => c.id === contact.id);
      if (isSelected) {
        return prev.filter(c => c.id !== contact.id);
      } else {
        return [...prev, contact];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts([...contacts]);
    }
  };

  const handleSendMessage = async () => {
    const isCloud = selectedApi === 'cloud-api';

    if (!selectedInstance) {
      showSnackbar(isCloud ? 'Selecione uma conta Cloud API' : 'Selecione uma instância', 'error');
      return;
    }

    if (isCloud) {
      if (!templateName.trim()) {
        showSnackbar('Informe o nome do template (como está no Meta)', 'error');
        return;
      }
    } else if (!messageText.trim()) {
      showSnackbar('Digite uma mensagem', 'error');
      return;
    }

    if (activeTab === 0 && !phoneNumber.trim()) {
      showSnackbar('Digite um número de telefone', 'error');
      return;
    }

    if (activeTab === 1 && selectedContacts.length === 0) {
      showSnackbar('Selecione pelo menos um contato', 'error');
      return;
    }

    setConfirmDialogOpen(true);
  };

  const confirmSendMessage = async () => {
    try {
      setSending(true);
      setConfirmDialogOpen(false);

      const isCloud = selectedApi === 'cloud-api';

      if (!isCloud) {
        const instance = instances.find(i => i.id === selectedInstance);
        if (!instance) {
          showSnackbar('Instância não encontrada', 'error');
          return;
        }
      } else {
        const cfg = cloudConfigs.find(c => c.id === selectedInstance);
        if (!cfg) {
          showSnackbar('Configuração Cloud API não encontrada', 'error');
          return;
        }
      }

      let parsedComponents: unknown = undefined;
      if (isCloud && templateComponentsJson.trim()) {
        try {
          parsedComponents = JSON.parse(templateComponentsJson.trim());
          if (!Array.isArray(parsedComponents)) {
            showSnackbar('Componentes do template devem ser um array JSON', 'error');
            setSending(false);
            return;
          }
        } catch {
          showSnackbar('JSON dos componentes inválido', 'error');
          setSending(false);
          return;
        }
      }

      if (activeTab === 0) {
        if (isCloud) {
          await axios.post(`${API_BASE_URL}/whatsapp-manual/send-cloud-template`, {
            cloud_api_config_id: selectedInstance,
            phone_number: phoneNumber,
            template_name: templateName.trim(),
            language_code: templateLanguage.trim() || 'pt_BR',
            components: parsedComponents,
          });
          showSnackbar(`Template enviado para ${phoneNumber}`, 'success');
          setPhoneNumber('');
          setTemplateName('');
          setTemplateComponentsJson('');
        } else {
          await axios.post(`${API_BASE_URL}/whatsapp-manual/send-message`, {
            instance_id: selectedInstance,
            phone_number: phoneNumber,
            message: messageText,
          });
          showSnackbar(`Mensagem enviada para ${phoneNumber}`, 'success');
          setPhoneNumber('');
          setMessageText('');
        }
      } else {
        if (isCloud) {
          const promises = selectedContacts.map(contact =>
            axios.post(`${API_BASE_URL}/whatsapp-manual/send-cloud-template`, {
              cloud_api_config_id: selectedInstance,
              phone_number: contact.phone,
              template_name: templateName.trim(),
              language_code: templateLanguage.trim() || 'pt_BR',
              components: parsedComponents,
            })
          );
          await Promise.all(promises);
          showSnackbar(`${selectedContacts.length} templates enviados com sucesso`, 'success');
          setSelectedContacts([]);
          setTemplateName('');
          setTemplateComponentsJson('');
        } else {
          const promises = selectedContacts.map(contact =>
            axios.post(`${API_BASE_URL}/whatsapp-manual/send-message`, {
              instance_id: selectedInstance,
              phone_number: contact.phone,
              message: messageText,
            })
          );
          await Promise.all(promises);
          showSnackbar(`${selectedContacts.length} mensagens enviadas com sucesso`, 'success');
          setSelectedContacts([]);
          setMessageText('');
        }
      }
    } catch (err: any) {
      console.error('Erro ao enviar mensagem:', err);
      showSnackbar(err.response?.data?.error || 'Erro ao enviar mensagem', 'error');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'success';
      case 'connecting': return 'warning';
      case 'disconnected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircleIcon />;
      case 'connecting': return <ErrorIcon />;
      case 'disconnected': return <ErrorIcon />;
      default: return <ErrorIcon />;
    }
  };

  // Se nenhuma API foi selecionada, mostrar tela de seleção
  if (!selectedApi) {
    return (
      <Box sx={{ 
        p: { xs: 2, md: 3 }, 
        backgroundColor: 'transparent', 
        minHeight: '100vh',
        color: 'text.primary',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* Header com título */}
        <Box sx={{ 
          mb: 4,
          textAlign: 'center',
        }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: '#000000',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 1,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}>
            <SendIcon sx={{ fontSize: { xs: 24, md: 32 }, color: '#3b82f6' }} />
            Envio Manual WhatsApp
          </Typography>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Selecione a API que deseja gerenciar
          </Typography>
        </Box>

        {/* Cards de seleção */}
        <Grid container spacing={3} sx={{ maxWidth: 800, width: '100%' }}>
          <Grid item xs={12} sm={6}>
            <Card 
              onClick={() => {
                setSelectedApi('z-api');
                localStorage.setItem('whatsapp-manual-api-selection', 'z-api');
              }}
              className="glass-blue-card"
              sx={{
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                }
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 2,
                  bgcolor: 'rgba(59, 130, 246, 0.2)',
                  border: '2px solid rgba(59, 130, 246, 0.4)',
                }}>
                  <WhatsAppIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
                </Avatar>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  color: '#000000',
                  mb: 1
                }}>
                  Z-API
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  mb: 3
                }}>
                  Envie mensagens manualmente usando a Z-API
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                    }
                  }}
                >
                  Selecionar Z-API
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card
              onClick={() => {
                setSelectedApi('cloud-api');
                localStorage.setItem('whatsapp-manual-api-selection', 'cloud-api');
              }}
              className="glass-blue-card"
              sx={{
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: 'rgba(99, 102, 241, 0.2)',
                    border: '2px solid rgba(99, 102, 241, 0.45)',
                  }}
                >
                  <CloudQueueIcon sx={{ fontSize: 40, color: '#6366f1' }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#000000', mb: 1 }}>
                  Cloud API (Meta)
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  Envio direto por template: número, nome do template e conta conectada
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    boxShadow: '0 4px 15px rgba(99, 102, 241, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                      boxShadow: '0 6px 20px rgba(99, 102, 241, 0.45)',
                    },
                  }}
                >
                  Selecionar Cloud API
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
    }}>

      {/* Header Sofisticado */}
      <Box className="glass-blue-card" sx={{
        mb: 3,
        p: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <IconButton
            onClick={() => {
              setSelectedApi(null);
              localStorage.removeItem('whatsapp-manual-api-selection');
            }}
            sx={{
              color: '#000000',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{
              fontWeight: 700,
              mb: 1,
              color: '#000000',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {selectedApi === 'cloud-api' ? (
                <CloudQueueIcon sx={{ fontSize: 32, color: '#6366f1' }} />
              ) : (
                <WhatsAppIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
              )}
              Envio Manual —{' '}
              {selectedApi === 'z-api' ? 'Z-API' : 'Cloud API'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {selectedApi === 'cloud-api'
                ? 'Envie um template aprovado pela Meta para um número ou vários leads; escolha a conta Cloud API conectada.'
                : 'Envie mensagens WhatsApp para números específicos ou leads do CRM usando Z-API'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
          sx={{
            background: 'rgba(59, 130, 246, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#000000',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }
          }}
        >
          Atualizar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {selectedApi === 'cloud-api' && cloudConfigs.length === 0 && !loading && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Nenhuma conta Cloud API com status &quot;active&quot;. Configure em{' '}
          <strong>Config WhatsApp → Cloud API</strong>.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Configurações */}
        <Grid item xs={12} md={4}>
          <Card className="glass-blue-card" sx={{
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, color: '#000000', fontWeight: 600 }}>
                Configurações
              </Typography>

              {/* Instância Z-API ou conta Cloud API */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel sx={{ color: '#64748b' }}>
                  {selectedApi === 'cloud-api' ? 'Conta Cloud API' : 'Instância WhatsApp'}
                </InputLabel>
                  <Select
                  value={selectedInstance}
                  onChange={(e) => setSelectedInstance(e.target.value)}
                  label={selectedApi === 'cloud-api' ? 'Conta Cloud API' : 'Instância WhatsApp'}
                  sx={{
                    color: '#000000',
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.4)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                      },
                      '&.Mui-focused': {
                        borderColor: 'rgba(59, 130, 246, 0.6)',
                        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: '#64748b',
                    },
                  }}
                >
                  {selectedApi === 'cloud-api'
                    ? cloudConfigs.map((cfg) => (
                        <MenuItem key={cfg.id} value={cfg.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CloudQueueIcon sx={{ fontSize: 20, color: '#6366f1' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {cfg.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {cfg.display_phone_number || cfg.phone_number || '—'} •{' '}
                                {(cfg.daily_sent ?? 0)}/{(cfg.daily_limit ?? '—')}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))
                    : instances.map((instance) => (
                        <MenuItem key={instance.id} value={instance.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WhatsAppIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {instance.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {instance.phone_number} • {instance.daily_sent}/{instance.daily_limit}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>

              {selectedApi === 'cloud-api' ? (
                <>
                  <Autocomplete
                    size="small"
                    options={cloudPresets}
                    loading={loadingPresets}
                    value={selectedPresetOption}
                    onChange={(_, v) => {
                      setSelectedPresetOption(v);
                      if (v) {
                        setTemplateName(v.template_name);
                        setTemplateLanguage(v.language_code || 'pt_BR');
                        setTemplateComponentsJson(
                          v.components && Array.isArray(v.components) && v.components.length > 0
                            ? JSON.stringify(v.components, null, 2)
                            : ''
                        );
                      }
                    }}
                    getOptionLabel={(o) => `${o.display_label} (${o.template_name})`}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Template salvo"
                        placeholder="Selecione um preset cadastrado (opcional)"
                        sx={{
                          mb: 1,
                          '& .MuiOutlinedInput-input': { color: '#000000' },
                          '& .MuiInputLabel-root': { color: '#64748b' },
                        }}
                      />
                    )}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Button
                      size="small"
                      startIcon={<BookmarkBorderIcon />}
                      onClick={openPresetManager}
                      disabled={!selectedInstance}
                    >
                      Cadastrar / gerenciar templates
                    </Button>
                  </Box>
                  <TextField
                    fullWidth
                    label="Nome do template"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Ex: hello_world, promo_black_friday"
                    sx={{ mb: 2, '& .MuiOutlinedInput-input': { color: '#000000' }, '& .MuiInputLabel-root': { color: '#64748b' } }}
                  />
                  <TextField
                    fullWidth
                    label="Idioma (language code)"
                    value={templateLanguage}
                    onChange={(e) => setTemplateLanguage(e.target.value)}
                    placeholder="pt_BR"
                    sx={{ mb: 2, '& .MuiOutlinedInput-input': { color: '#000000' }, '& .MuiInputLabel-root': { color: '#64748b' } }}
                  />
                  <TextField
                    fullWidth
                    label="Componentes (opcional, JSON)"
                    multiline
                    rows={3}
                    value={templateComponentsJson}
                    onChange={(e) => setTemplateComponentsJson(e.target.value)}
                    placeholder='[{"type":"body","parameters":[{"type":"text","text":"João"}]}]'
                    helperText="Só necessário se o template tiver variáveis. Deixe vazio para usar o padrão do Meta."
                    sx={{ mb: 3, '& .MuiOutlinedInput-input': { color: '#000000' }, '& .MuiInputLabel-root': { color: '#64748b' } }}
                  />
                </>
              ) : (
              <TextField
                fullWidth
                label="Mensagem"
                multiline
                rows={6}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Digite sua mensagem aqui..."
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(59, 130, 246, 0.08)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#000000',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(59, 130, 246, 0.4)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(59, 130, 246, 0.6)',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& textarea': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: 'rgba(59, 130, 246, 0.8)',
                    },
                  },
                }}
              />
              )}

              {/* Botões de Ação */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setPreviewDialogOpen(true)}
                  disabled={selectedApi === 'cloud-api' || !messageText.trim()}
                  sx={{
                    background: 'rgba(59, 130, 246, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#000000',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }
                  }}
                >
                  Preview
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSendMessage}
                  disabled={
                    sending ||
                    !selectedInstance ||
                    (selectedApi === 'cloud-api'
                      ? !templateName.trim()
                      : !messageText.trim())
                  }
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                    }
                  }}
                >
                  {sending ? 'Enviando...' : 'Enviar'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Conteúdo Principal */}
        <Grid item xs={12} md={8}>
          <Card className="glass-blue-card" sx={{
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}>
            <CardContent>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  mb: 3,
                  '& .MuiTab-root': {
                    color: '#64748b',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&.Mui-selected': {
                      color: '#3b82f6',
                      fontWeight: 700,
                    },
                    '&:hover': {
                      color: 'rgba(255, 255, 255, 0.9)',
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#3b82f6',
                    height: 3,
                    borderRadius: '2px 2px 0 0',
                  }
                }}
              >
                <Tab label="Envio por Número" icon={<PhoneIcon />} />
                <Tab label="Envio para Leads CRM" icon={<PeopleIcon />} />
              </Tabs>

              {activeTab === 0 ? (
                /* Envio por Número */
                <Box>
                  <TextField
                    fullWidth
                    label="Número de Telefone"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Ex: 5511999999999"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(59, 130, 246, 0.08)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: '8px',
                        color: '#000000',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'rgba(59, 130, 246, 0.4)',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                        },
                        '&.Mui-focused': {
                          borderColor: 'rgba(59, 130, 246, 0.6)',
                          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                        },
                        '& fieldset': {
                          border: 'none',
                        },
                        '& input': {
                          color: '#000000',
                          '&::placeholder': {
                            color: '#94a3b8',
                            opacity: 1,
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748b',
                        '&.Mui-focused': {
                          color: 'rgba(59, 130, 246, 0.8)',
                        },
                      },
                    }}
                  />
                </Box>
              ) : (
                /* Envio para Leads CRM */
                <Box>
                  {/* Filtros */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#000000', fontWeight: 600 }}>
                      Filtros
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Buscar"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 1 }} />
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              background: 'rgba(59, 130, 246, 0.08)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              '&:hover': {
                                border: '1px solid rgba(59, 130, 246, 0.5)',
                              },
                              '&.Mui-focused': {
                                border: '1px solid #3b82f6',
                                boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                              }
                            },
                            '& .MuiInputLabel-root': {
                              color: '#64748b',
                              '&.Mui-focused': {
                                color: '#3b82f6',
                              }
                            },
                            '& .MuiOutlinedInput-input': {
                              color: '#000000',
                            }
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showOnlyWithWhatsApp}
                              onChange={(e) => setShowOnlyWithWhatsApp(e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#3b82f6',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#3b82f6',
                                }
                              }}
                            />
                          }
                          label="Apenas com WhatsApp"
                          sx={{ color: '#000000' }}
                        />
                      </Grid>
                      
                      {/* Filtros por Estágio */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel sx={{ color: '#64748b' }}>
                            Filtrar por Estágio
                          </InputLabel>
                          <Select
                            multiple
                            value={selectedStages}
                            onChange={(e) => setSelectedStages(e.target.value as string[])}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => {
                                  const stage = crmStages.find(s => s.id === value);
                                  return (
                                    <Chip
                                      key={value}
                                      label={stage?.name || value}
                                      size="small"
                                      sx={{
                                        background: 'rgba(59, 130, 246, 0.2)',
                                        color: '#3b82f6',
                                        border: '1px solid rgba(59, 130, 246, 0.3)',
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                background: 'rgba(59, 130, 246, 0.08)',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                border: '1px solid rgba(59, 130, 246, 0.2)',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: 'rgba(59, 130, 246, 0.4)',
                                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                                },
                                '&.Mui-focused': {
                                  borderColor: 'rgba(59, 130, 246, 0.6)',
                                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                                },
                                '& fieldset': {
                                  border: 'none',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: '#64748b',
                                '&.Mui-focused': {
                                  color: '#3b82f6',
                                }
                              }
                            }}
                          >
                            {crmStages.map((stage) => (
                              <MenuItem key={stage.id} value={stage.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: stage.color,
                                    }}
                                  />
                                  <Typography>{stage.name}</Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      {/* Filtros por Pipeline */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel sx={{ color: '#64748b' }}>
                            Filtrar por Pipeline
                          </InputLabel>
                          <Select
                            multiple
                            value={selectedPipelines}
                            onChange={(e) => setSelectedPipelines(e.target.value as string[])}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => {
                                  const pipeline = crmPipelines.find(p => p.id === value);
                                  return (
                                    <Chip
                                      key={value}
                                      label={pipeline?.name || value}
                                      size="small"
                                      sx={{
                                        background: 'rgba(59, 130, 246, 0.2)',
                                        color: '#3b82f6',
                                        border: '1px solid rgba(59, 130, 246, 0.3)',
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                background: 'rgba(59, 130, 246, 0.08)',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                border: '1px solid rgba(59, 130, 246, 0.2)',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: 'rgba(59, 130, 246, 0.4)',
                                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                                },
                                '&.Mui-focused': {
                                  borderColor: 'rgba(59, 130, 246, 0.6)',
                                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                                },
                                '& fieldset': {
                                  border: 'none',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: '#64748b',
                                '&.Mui-focused': {
                                  color: '#3b82f6',
                                }
                              }
                            }}
                          >
                            {crmPipelines.map((pipeline) => (
                              <MenuItem key={pipeline.id} value={pipeline.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: pipeline.color,
                                    }}
                                  />
                                  <Typography>{pipeline.name}</Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectAll}
                        sx={{
                          background: 'rgba(59, 130, 246, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#000000',
                          '&:hover': {
                            background: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#3b82f6',
                          }
                        }}
                      >
                        {selectedContacts.length === contacts.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                      </Button>
                      
                      <Chip
                        label={`${selectedContacts.length} selecionados`}
                        color="primary"
                        sx={{
                          background: 'rgba(59, 130, 246, 0.2)',
                          color: '#3b82f6',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Lista de Contatos */}
                  <List sx={{ 
                    maxHeight: 400, 
                    overflow: 'auto',
                    background: 'rgba(31, 41, 55, 0.2)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(59, 130, 246, 0.1)',
                    borderRadius: '8px',
                    padding: 1,
                  }}>
                    {contacts.map((contact) => (
                      <ListItem
                        key={contact.id}
                        sx={{
                          background: selectedContacts.some(c => c.id === contact.id) 
                            ? 'rgba(59, 130, 246, 0.1)' 
                            : 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 2,
                          mb: 1,
                          '&:hover': {
                            background: 'rgba(59, 130, 246, 0.05)',
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: '#3b82f6' }}>
                            {contact.avatar_url ? (
                              <img src={contact.avatar_url} alt={contact.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <PersonIcon />
                            )}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={contact.name}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {contact.phone}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {contact.stage_name} • {contact.pipeline_name}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Checkbox
                            checked={selectedContacts.some(c => c.id === contact.id)}
                            onChange={() => handleContactToggle(contact)}
                            sx={{
                              color: '#94a3b8',
                              '&.Mui-checked': {
                                color: '#3b82f6',
                              }
                            }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Presets Cloud API — cadastro para lista de envio manual */}
      <Dialog open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#000000' }}>Templates salvos (Cloud API)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Cada preset fica ligado à conta Cloud API selecionada na coluna ao lado. Use o mesmo nome do template aprovado na Meta.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={resetPresetFormFields}>
              Novo
            </Button>
            <Button size="small" variant="text" onClick={fillPresetFormFromSendFields}>
              Copiar campos do envio atual
            </Button>
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Nome na lista"
            value={presetForm.display_label}
            onChange={(e) => setPresetForm((p) => ({ ...p, display_label: e.target.value }))}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Nome no Meta"
            value={presetForm.template_name}
            onChange={(e) => setPresetForm((p) => ({ ...p, template_name: e.target.value }))}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Idioma"
            value={presetForm.language_code}
            onChange={(e) => setPresetForm((p) => ({ ...p, language_code: e.target.value }))}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Componentes (JSON opcional)"
            multiline
            rows={3}
            value={presetForm.components_json}
            onChange={(e) => setPresetForm((p) => ({ ...p, components_json: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" onClick={saveCloudPreset} disabled={presetSaving} sx={{ mb: 3 }}>
            {presetSaving ? 'Salvando…' : presetForm.editingId ? 'Atualizar preset' : 'Salvar preset'}
          </Button>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Cadastrados nesta conta
          </Typography>
          {cloudPresets.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum template salvo ainda.
            </Typography>
          ) : (
            <List dense disablePadding>
              {cloudPresets.map((p) => (
                <ListItem
                  key={p.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                  }}
                  secondaryAction={
                    <Box>
                      <IconButton edge="end" aria-label="editar" onClick={() => editCloudPreset(p)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton edge="end" aria-label="remover" onClick={() => deleteCloudPreset(p.id)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText primary={p.display_label} secondary={`${p.template_name} • ${p.language_code}`} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresetDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmação */}
      <Dialog 
        open={confirmDialogOpen} 
        onClose={() => setConfirmDialogOpen(false)}
        PaperProps={{
          className: "glass-blue-card",
          sx: {
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ color: '#000000', borderBottom: '1px solid rgba(59, 130, 246, 0.1)' }}>
          Confirmar Envio
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#000000' }}>
            {selectedApi === 'cloud-api'
              ? activeTab === 0
                ? `Enviar template "${templateName.trim()}" para ${phoneNumber}?`
                : `Enviar template "${templateName.trim()}" para ${selectedContacts.length} contatos?`
              : activeTab === 0
                ? `Enviar mensagem para ${phoneNumber}?`
                : `Enviar mensagem para ${selectedContacts.length} contatos selecionados?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
          <Button onClick={confirmSendMessage} variant="contained" color="primary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar?.open || false}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar?.severity || 'info'}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppManual;
